import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@busy/api';
import { useT } from '../lib/i18n';

export function SignIn() {
  const nav = useNavigate();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) return setError(error.message);
    nav('/today', { replace: true });
  }

  async function signInWith(provider: 'apple' | 'google') {
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/today` },
    });
    if (error) setError(error.message);
  }

  return (
    <AuthShell quote={t('auth.quoteSignin')} cite={t('auth.cite')}>
      <h2 className="font-display text-3xl md:text-4xl text-navy-900 dark:text-gold-500 mb-1">
        {t('auth.signin.title')}
      </h2>
      <p className="text-ink-500 text-[15px] mb-7">{t('auth.signin.sub')}</p>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => signInWith('apple')}
          className="w-full h-12 rounded-xl bg-black text-white font-bold inline-flex items-center justify-center gap-2"
        >
          {t('auth.continueApple')}
        </button>
        <button
          type="button"
          onClick={() => signInWith('google')}
          className="w-full h-12 rounded-xl border-[1.5px] border-line-strong bg-surface font-bold text-ink-900 hover:bg-bg-alt"
        >
          {t('auth.continueGoogle')}
        </button>
      </div>

      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-line" />
        <span className="text-[11px] text-ink-500 uppercase tracking-[0.14em] font-bold">{t('auth.or')}</span>
        <div className="flex-1 h-px bg-line" />
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <input
          type="email"
          placeholder={t('auth.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full h-12 px-4 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/20 dark:focus:border-gold-500 dark:focus:ring-gold-500/20"
        />
        <input
          type="password"
          placeholder={t('auth.password')}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full h-12 px-4 rounded-xl border-[1.5px] border-line-strong bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/20 dark:focus:border-gold-500 dark:focus:ring-gold-500/20"
        />
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-12 rounded-xl bg-navy-900 text-white font-bold dark:bg-gold-500 dark:text-navy-900 disabled:opacity-60"
        >
          {loading ? t('auth.signin.busy') : t('auth.signin.button')}
        </button>
      </form>

      <p className="text-center text-sm text-ink-500 mt-6">
        {t('auth.signin.switch')}{' '}
        <Link to="/sign-up" className="text-navy-900 dark:text-gold-500 font-bold">
          {t('auth.signin.create')}
        </Link>
      </p>
    </AuthShell>
  );
}

// Shared two-column shell for SignIn + SignUp: navy quote panel on the left,
// white form panel on the right. Mirrors demo's .auth-shell (DEMO_REFERENCE.html:708).
function AuthShell({
  quote,
  cite,
  children,
}: {
  quote: string;
  cite: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-[100dvh] md:grid md:grid-cols-2 bg-bg">
      {/* Quote panel — hidden on mobile, full-screen left column on desktop */}
      <aside
        className="hidden md:flex relative items-center justify-center p-10 text-white overflow-hidden"
        style={{
          background:
            'radial-gradient(ellipse at 30% 30%, rgba(212,169,66,.18), transparent 55%), radial-gradient(ellipse at 70% 70%, rgba(200,16,46,.18), transparent 55%), #0d1424',
        }}
      >
        <div className="relative z-10 text-center max-w-md">
          <img
            src="/assets/busy-mascot.png"
            alt=""
            className="w-full max-w-[320px] mx-auto mb-7"
            style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,.55))' }}
          />
          <p className="font-medium italic text-[22px] leading-snug text-white mb-4">{quote}</p>
          <div className="text-gold-400 font-bold tracking-[0.14em] text-xs">{cite}</div>
        </div>
      </aside>
      {/* Form panel */}
      <main className="flex items-center justify-center p-6 md:p-10 min-h-[100dvh] md:min-h-0 overflow-y-auto">
        <div className="w-full max-w-md bg-surface rounded-3xl p-8 md:p-10 shadow-md border border-line">
          {/* Brand mark — visible on mobile (where quote panel is hidden) */}
          <div className="md:hidden flex items-center gap-2.5 mb-6">
            <span className="w-9 h-9 rounded-[10px] grid place-items-center overflow-hidden">
              <img src="/assets/busy-icon.png" alt="" className="w-full h-full object-cover" />
            </span>
            <span className="font-display tracking-[0.04em] text-lg text-navy-900 dark:text-gold-500">
              BUSY <span className="text-gold-400">BARBER</span>
            </span>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
